'use client';

/**
 * CallControls — the three-button control row for the v3 CallScreen
 * (design turn 11a, mic management restyled per turn 12): Mute, End, CC.
 * Purely presentational — owns no cross-component state, just renders
 * isMuted/showCC and forwards taps to the handlers CallScreen wires to
 * ChatContext (toggleMute / toggleCall / setShowCC / switchMic).
 *
 * `size="desktop"` adds a 12px text label under each button (Figma's
 * desktop left-column treatment); `size="mobile"` (default) is icon-only.
 *
 * Mic switcher (Turn-12 12a): a small chevron-up badge pinned to the Mute
 * circle's bottom-right. It opens the device menu UPWARD as a dropdown on
 * desktop and as a BOTTOM SHEET on mobile — chosen by the same `size` prop
 * CallScreen already sets from its own desktop/mobile matchMedia split, so
 * the boundary can never disagree with the layout. Devices are grouped via
 * mic-selection's micDeviceGroup (single source of truth with the
 * auto-fallback ranking): main list first, then a separated, dimmed
 * "Virtual & loopback" group.
 *
 * Switch-confirmation moment (12c): when the active device CHANGES while
 * mounted (user switch OR auto-fallback), the Mute circle plays the
 * design's one-shot micPop and a micRing ring expands from it — see
 * `cueKey` below. All colors/sizes/shadows extracted verbatim from
 * design-v2.html.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Check,
  ChevronUp,
  Headphones,
  Laptop,
  Mic,
  MicOff,
  SlidersHorizontal,
  Smartphone,
  X,
  type LucideIcon,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import { V3_COLORS, PRESSABLE } from '@/lib/design-tokens';
import {
  MicDeviceGroup,
  buildOutputGroupIds,
  classifyMicDevice,
} from '@/lib/mic-selection';

export interface CallControlsProps {
  onMute: () => void;
  onEnd: () => void;
  onToggleCC: () => void;
  isMuted: boolean;
  showCC: boolean;
  /** Audioinput devices for the switcher's rows — see useCallMode's
   *  `audioInputs`. The switcher only renders when there's more than one. */
  micDevices: MediaDeviceInfo[];
  /** FULL enumerateDevices() list (inputs AND outputs) — micDeviceGroup's
   *  headset classification needs the audiooutput groupIds. */
  allDevices: MediaDeviceInfo[];
  /** deviceId of the currently-capturing track — see useCallMode's
   *  `activeMicId`. Drives the active-row treatment. */
  activeMicId: string | null;
  /** User picked a mic from the menu — see useCallMode's `switchMic`. */
  onSelectMic: (deviceId: string) => void;
  /** 'desktop' adds text labels under each button and renders the device
   *  menu as an upward dropdown; 'mobile' (default) is icon-only and
   *  renders the menu as a bottom sheet. */
  size?: 'desktop' | 'mobile';
  className?: string;
  /** [FB34] True until the call reaches `live` (callPhase !== 'live') —
   *  there's no audio flowing yet to mute or caption during
   *  `connecting`/`failed`. Dimmed + inert rather than unmounted, so the
   *  control row's layout never shifts as the call comes up (End stays
   *  fully active throughout — see call-screen.tsx). Also suppresses the
   *  mic switcher (nothing to switch to yet). Placeholder dimming only —
   *  the Turn-16 visual pass owns the exact "wake" treatment. */
  muteDisabled?: boolean;
  /** [FB34] Same as `muteDisabled`, for the CC button. */
  ccDisabled?: boolean;
}

const DIAMETERS = {
  desktop: { mute: 52, end: 62, cc: 52 },
  mobile: { mute: 46, end: 58, cc: 46 },
} as const;

/** 12a badge geometry per size — mobile mock uses an 18px badge on the
 *  46px Mute circle; the 2px "cutout" border matches the surface behind
 *  the controls in each layout. */
const BADGE = {
  desktop: {
    size: 22,
    offset: -3,
    chevron: 12,
    stroke: 2.4,
    cutout: V3_COLORS.surfaceWarm,
  },
  mobile: {
    size: 18,
    offset: -2,
    chevron: 10,
    stroke: 2.6,
    cutout: V3_COLORS.surfaceSidebar,
  },
} as const;

const GROUP_ICONS: Record<MicDeviceGroup, LucideIcon> = {
  [MicDeviceGroup.HEADSET]: Headphones,
  [MicDeviceGroup.BUILT_IN]: Laptop,
  [MicDeviceGroup.STANDARD]: Mic,
  [MicDeviceGroup.PHONE]: Smartphone,
  [MicDeviceGroup.VIRTUAL]: SlidersHorizontal,
};

interface ClassifiedDevice {
  device: MediaDeviceInfo;
  group: MicDeviceGroup;
}

/** Per-size Tailwind class fragments for MicRow. FULL literal strings (not
 *  templated) so the JIT scanner sees every class — same rule as PRESSABLE
 *  in design-tokens.ts. */
const MIC_ROW_CLASSES = {
  desktop: {
    tile: 'size-[30px] rounded-[9px]',
    icon: 'size-[16px]',
    nameActive: 'text-[13.5px] font-semibold text-[#E36001]',
    nameVirtual: 'text-[13px] font-medium text-[#8a7c68]',
    name: 'text-[13.5px] font-medium text-[#5b6072]',
    check: 'size-[15px]',
  },
  mobile: {
    tile: 'size-[34px] rounded-[10px]',
    icon: 'size-[17px]',
    nameActive: 'text-[14px] font-semibold text-[#E36001]',
    nameVirtual: 'text-[13.5px] font-medium text-[#8a7c68]',
    name: 'text-[14px] font-medium text-[#31364E]',
    check: 'size-[17px]',
  },
} as const;

/** Shared row content for the device menu — the dropdown (desktop) and the
 *  bottom sheet (mobile) render identical anatomy inside different
 *  interactive wrappers (DropdownMenuItem vs <button>), so the anatomy
 *  lives once here. */
function MicRow({
  device,
  group,
  isActive,
  index,
  size,
}: {
  device: MediaDeviceInfo;
  group: MicDeviceGroup;
  isActive: boolean;
  index: number;
  size: 'desktop' | 'mobile';
}) {
  const c = MIC_ROW_CLASSES[size];
  const isVirtual = group === MicDeviceGroup.VIRTUAL;
  const Icon = GROUP_ICONS[group];
  return (
    <>
      <span
        className={cn(
          'flex shrink-0 items-center justify-center border-[1px]',
          c.tile,
          isVirtual
            ? 'border-[#efe6d6] bg-[#faf5ec]'
            : isActive
              ? 'border-[#F3D9BE] bg-white'
              : 'border-[#f0e7da] bg-white'
        )}
      >
        <Icon
          strokeWidth={2}
          className={cn(
            c.icon,
            isVirtual
              ? 'text-[#a99a82]'
              : isActive
                ? 'text-[#E36001]'
                : 'text-[#6b7184]'
          )}
        />
      </span>
      <span
        className={cn(
          'min-w-0 flex-1 truncate',
          isActive ? c.nameActive : isVirtual ? c.nameVirtual : c.name
        )}
      >
        {device.label || `Microphone ${index + 1}`}
      </span>
      {group === MicDeviceGroup.BUILT_IN && !isActive && (
        <span className="shrink-0 text-[10.5px] font-semibold text-[#a99a82]">
          Built-in
        </span>
      )}
      {isActive && (
        <Check
          strokeWidth={2.6}
          className={cn('shrink-0 text-[#E36001]', c.check)}
        />
      )}
    </>
  );
}

export function CallControls({
  onMute,
  onEnd,
  onToggleCC,
  isMuted,
  showCC,
  micDevices,
  allDevices,
  activeMicId,
  onSelectMic,
  size = 'mobile',
  className,
  muteDisabled = false,
  ccDisabled = false,
}: CallControlsProps) {
  const isDesktop = size === 'desktop';
  const d = DIAMETERS[size];
  const b = BADGE[size];
  // [FB34] Nothing to switch TO yet while muted-out (pre-live) — same
  // reasoning as the mute/CC dimming above.
  const showMicSwitcher = micDevices.length > 1 && !muteDisabled;
  const [menuOpen, setMenuOpen] = useState(false);

  // Mic-switch cue (12c): incremented on every OBSERVED activeMicId change
  // (not the initial mount — prevMicIdRef seeds with the first value, so
  // opening the call screen never pops). The ring span and the Mute
  // circle's chrome span are keyed on cueKey, so each increment remounts
  // them and restarts the one-shot micRing/micPop animations. Skipped while
  // the switcher is hidden (single-mic setups) — no orphan ring.
  const [cueKey, setCueKey] = useState(0);
  const prevMicIdRef = useRef(activeMicId);
  useEffect(() => {
    if (prevMicIdRef.current === activeMicId) return;
    prevMicIdRef.current = activeMicId;
    if (micDevices.length > 1) {
      setCueKey((k) => k + 1);
    }
  }, [activeMicId, micDevices.length]);

  // Group devices for the menu via the same tier logic as the
  // auto-fallback (mic-selection.ts). allDevices may still be empty for a
  // beat right after call start (async enumerate) — fall back to the input
  // list so classification never sees an empty universe. Memoized (and the
  // output-groupId set built ONCE) because CallControls re-renders at
  // caption-update rate during a live call — the device lists only change
  // on enumerate/devicechange.
  const { mainRows, virtualRows } = useMemo(() => {
    const universe = allDevices.length > 0 ? allDevices : micDevices;
    const outputGroupIds = buildOutputGroupIds(universe);
    const classified: ClassifiedDevice[] = micDevices.map((device) => ({
      device,
      group: classifyMicDevice(device, outputGroupIds),
    }));
    return {
      mainRows: classified.filter((r) => r.group !== MicDeviceGroup.VIRTUAL),
      virtualRows: classified.filter((r) => r.group === MicDeviceGroup.VIRTUAL),
    };
  }, [micDevices, allDevices]);

  // ── 12a badge (shared trigger for dropdown + sheet) ──────────────
  const switcherBadge = (
    <button
      type="button"
      aria-label="Change microphone"
      // PRESSABLE gives the shared press scale on the button; all color/
      // shadow chrome lives on the inner span so its transitions never
      // fight the transform (see PRESSABLE's doc comment).
      className={cn('group absolute z-10', PRESSABLE)}
      style={{
        width: b.size,
        height: b.size,
        right: b.offset,
        bottom: b.offset,
      }}
    >
      <span
        className={cn(
          'flex size-full items-center justify-center rounded-full transition-[background-color,color,box-shadow] duration-[160ms] ease-out',
          menuOpen
            ? 'bg-[#E36001] text-white'
            : 'bg-white text-[#8a7c68] group-hover:text-[#E36001] group-active:bg-[#FFF0E4] group-active:text-[#E36001]'
        )}
        style={{
          border: `2px solid ${b.cutout}`,
          boxShadow: menuOpen
            ? '0 4px 10px -3px rgba(227,96,1,.7)'
            : '0 3px 7px -3px rgba(49,54,78,.4)',
        }}
      >
        <ChevronUp size={b.chevron} strokeWidth={b.stroke} />
      </span>
    </button>
  );

  // ── 12a menu rows — MicRow anatomy inside per-surface wrappers ────
  const renderDesktopRow = (
    { device, group }: ClassifiedDevice,
    index: number
  ) => {
    const isActive = device.deviceId === activeMicId;
    return (
      <DropdownMenuItem
        key={device.deviceId || `mic-${index}`}
        onSelect={() => void onSelectMic(device.deviceId)}
        className={cn(
          'cursor-pointer gap-[11px] rounded-[11px] px-[11px] py-[10px] focus:bg-[#FBF1E2] data-[highlighted]:bg-[#FBF1E2]',
          group === MicDeviceGroup.VIRTUAL && 'py-[8px] opacity-[0.7]',
          isActive &&
            'bg-[#FFF0E4] focus:bg-[#FFF0E4] data-[highlighted]:bg-[#FFF0E4]'
        )}
      >
        <MicRow
          device={device}
          group={group}
          isActive={isActive}
          index={index}
          size="desktop"
        />
      </DropdownMenuItem>
    );
  };

  const renderMobileRow = (
    { device, group }: ClassifiedDevice,
    index: number
  ) => {
    const isActive = device.deviceId === activeMicId;
    return (
      <button
        type="button"
        key={device.deviceId || `mic-${index}`}
        onClick={() => {
          void onSelectMic(device.deviceId);
          setMenuOpen(false);
        }}
        className={cn(
          'flex w-full items-center gap-[12px] rounded-[13px] p-[12px] text-left',
          group === MicDeviceGroup.VIRTUAL && 'py-[10px] opacity-[0.7]',
          isActive && 'bg-[#FFF0E4]'
        )}
      >
        <MicRow
          device={device}
          group={group}
          isActive={isActive}
          index={index}
          size="mobile"
        />
      </button>
    );
  };

  const virtualSeparator = (padded: boolean) => (
    <div
      className={cn(
        'border-t-[1px] border-[#f0e7da]',
        padded
          ? 'mx-[6px] mb-[4px] mt-[8px] pt-[10px]'
          : 'mx-[6px] mb-[3px] mt-[6px] pt-[9px]'
      )}
    >
      <span className="text-[10.5px] font-semibold uppercase tracking-[0.05em] text-[#a99a82]">
        Virtual &amp; loopback
      </span>
    </div>
  );

  return (
    <div
      className={cn('flex items-start justify-center gap-[22px]', className)}
    >
      {/* Mute */}
      <div className="flex flex-col items-center gap-1.5">
        <div className="relative">
          {/* 12c confirmation ring — expands from the Mute circle. Static
              opacity-[0] (arbitrary — Bootstrap's !important .opacity-0
              collides) keeps it invisible for reduced-motion users; the
              micRing keyframes own transform+opacity while playing. Keyed
              so each observed switch restarts the one-shot. Rendered BEFORE
              the button so it paints behind the circle and emerges as it
              expands past it. */}
          {cueKey > 0 && (
            <span
              key={`mic-cue-ring-${cueKey}`}
              aria-hidden="true"
              className="pointer-events-none absolute left-1/2 top-1/2 rounded-full border-[2.5px] border-[#E36001] opacity-[0] motion-safe:animate-mic-cue-ring"
              style={{ width: d.mute, height: d.mute }}
            />
          )}
          <button
            type="button"
            onClick={onMute}
            disabled={muteDisabled}
            aria-label={isMuted ? 'Unmute microphone' : 'Mute microphone'}
            aria-pressed={isMuted}
            // PRESSABLE press scale on the button; the circle CHROME
            // (background/border/shadow + the 12c pop) lives on the keyed
            // inner span so remounting it restarts micPop without touching
            // the button (and its transitions never fight the transform —
            // see PRESSABLE's doc comment). [Turn 16] `muteDisabled` dims +
            // inerts the whole circle — `opacity-[.5]` arbitrary (Bootstrap
            // `.opacity-50` collision, same rule as design-tokens.ts's
            // PRESSABLE doc comment), on top of the inner span's literal
            // dimmed chrome swap below (16a: this ISN'T a plain opacity fade
            // over the live-state colors, the design calls out its own
            // resting hex set).
            className={cn(
              'relative flex shrink-0',
              PRESSABLE,
              muteDisabled && 'pointer-events-none opacity-[.5]'
            )}
            style={{ width: d.mute, height: d.mute }}
          >
            <span
              key={`mic-cue-pop-${cueKey}`}
              className={cn(
                'flex size-full items-center justify-center rounded-full border-[1px] transition-[background-color,border-color,box-shadow] duration-[160ms] ease-out',
                cueKey > 0 && 'motion-safe:animate-mic-cue-pop'
              )}
              style={{
                // [Turn 16] Dimmed chrome wins outright — nothing to mute
                // yet, so isMuted/menuOpen's live-state tints don't apply.
                backgroundColor: muteDisabled
                  ? V3_COLORS.callDimmedBg
                  : menuOpen
                    ? '#FFF7EF'
                    : isMuted
                      ? '#FFF0E4'
                      : '#FFFFFF',
                borderColor: muteDisabled
                  ? V3_COLORS.callDimmedBorder
                  : menuOpen || isMuted
                    ? V3_COLORS.orange
                    : '#ece6dc',
                boxShadow:
                  !muteDisabled && menuOpen
                    ? '0 10px 22px -12px rgba(227,96,1,.5)'
                    : undefined,
              }}
            >
              {isMuted ? (
                <MicOff
                  size={21}
                  strokeWidth={2}
                  color={
                    muteDisabled ? V3_COLORS.callDimmedIcon : V3_COLORS.ink
                  }
                />
              ) : (
                <Mic
                  size={21}
                  strokeWidth={2}
                  color={
                    muteDisabled ? V3_COLORS.callDimmedIcon : V3_COLORS.ink
                  }
                />
              )}
            </span>
          </button>

          {showMicSwitcher &&
            (isDesktop ? (
              <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
                <DropdownMenuTrigger asChild>
                  {switcherBadge}
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  side="top"
                  align="center"
                  sideOffset={10}
                  // Explicit warm surface per design-v2.html 12a —
                  // intentional Figma tones, kept even though the historical
                  // transparent-menu bug (tailwind.config was missing the
                  // `popover` color key, so `bg-popover` compiled to
                  // nothing) is now fixed at the config level.
                  className="w-[300px] overflow-hidden rounded-[16px] border-[1px] border-[#eadfce] bg-[#FFFAF5] p-0"
                  style={{
                    boxShadow:
                      '0 24px 54px -18px rgba(49,54,78,.42), 0 4px 12px -8px rgba(49,54,78,.22)',
                  }}
                >
                  <div className="flex items-center justify-between border-b-[1px] border-[#f0e7da] px-[16px] pb-[10px] pt-[13px]">
                    <span className="font-[family-name:var(--font-lora)] text-[14.5px] font-semibold text-[#31364E]">
                      Microphone
                    </span>
                    <span className="text-[11px] text-[#8a7c68]">
                      {micDevices.length} available
                    </span>
                  </div>
                  <div className="p-[6px]">
                    {mainRows.map(renderDesktopRow)}
                    {virtualRows.length > 0 && (
                      <>
                        {virtualSeparator(false)}
                        {virtualRows.map(renderDesktopRow)}
                      </>
                    )}
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
                <SheetTrigger asChild>{switcherBadge}</SheetTrigger>
                <SheetContent
                  side="bottom"
                  showCloseButton={false}
                  aria-describedby={undefined}
                  // Same explicit-surface rule as the dropdown above; sheet
                  // geometry from design-v2.html 12a mobile panel.
                  className="gap-0 rounded-t-[26px] border-t-0 bg-[#FFFAF5] px-[12px] pb-[24px] pt-[8px]"
                  style={{ boxShadow: '0 -20px 44px -18px rgba(49,54,78,.4)' }}
                >
                  <div
                    className="mx-auto mb-[8px] h-[4px] w-[38px] rounded-[3px] bg-[#e6dccd]"
                    aria-hidden="true"
                  />
                  <div className="flex items-center justify-between px-[8px] pb-[10px] pt-[4px]">
                    <SheetTitle className="font-[family-name:var(--font-lora)] text-[16px] font-semibold text-[#31364E]">
                      Microphone
                    </SheetTitle>
                    <span className="text-[11px] text-[#8a7c68]">
                      {micDevices.length} available
                    </span>
                  </div>
                  {mainRows.map(renderMobileRow)}
                  {virtualRows.length > 0 && (
                    <>
                      {virtualSeparator(true)}
                      {virtualRows.map(renderMobileRow)}
                    </>
                  )}
                </SheetContent>
              </Sheet>
            ))}
        </div>
        {isDesktop && (
          <span
            className={cn(
              'text-[12px]',
              // [Turn 16] muteDisabled's text-[#b3a894] literal matches
              // design-tokens.ts's `callDimmedText` (arbitrary-class strings
              // can't reference a JS constant — see PRESSABLE's doc comment
              // for this file's rule on literal Tailwind classes).
              muteDisabled
                ? 'font-medium text-[#b3a894]'
                : menuOpen
                  ? 'font-semibold text-[#E36001]'
                  : 'font-medium text-[#8a7c68]'
            )}
          >
            {isMuted ? 'Unmute' : 'Mute'}
          </span>
        )}
      </div>

      {/* End */}
      <div className="flex flex-col items-center gap-1.5">
        <button
          type="button"
          onClick={onEnd}
          aria-label="End call"
          className={cn(
            'flex shrink-0 items-center justify-center rounded-full text-white',
            PRESSABLE
          )}
          style={{
            width: d.end,
            height: d.end,
            backgroundColor: V3_COLORS.endRed,
            boxShadow: '0 12px 26px -10px rgba(217,72,59,.7)',
          }}
        >
          <X size={24} />
        </button>
        {isDesktop && (
          <span className="text-[12px] font-medium text-[#8a7c68]">End</span>
        )}
      </div>

      {/* CC */}
      <div className="flex flex-col items-center gap-1.5">
        <button
          type="button"
          onClick={onToggleCC}
          disabled={ccDisabled}
          aria-label={showCC ? 'Hide captions' : 'Show captions'}
          aria-pressed={showCC}
          // Shared press feel merged into the existing color transition —
          // can't just concatenate PRESSABLE's transition-transform class
          // here, it would clobber transition-colors (see PRESSABLE's doc
          // comment). [Turn 16] `ccDisabled` dims + inerts the button —
          // `opacity-[.5]` arbitrary (Bootstrap `.opacity-50` collision,
          // same rule as the Mute button above), on top of the literal
          // dimmed chrome swap below.
          className={cn(
            'flex shrink-0 items-center justify-center rounded-full border-[1px] transition-[background-color,border-color,transform] duration-[160ms] ease-out motion-safe:active:scale-[0.96]',
            ccDisabled && 'pointer-events-none opacity-[.5]'
          )}
          style={{
            width: d.cc,
            height: d.cc,
            backgroundColor: ccDisabled
              ? V3_COLORS.callDimmedBg
              : showCC
                ? '#FFF0E4'
                : '#FFFFFF',
            borderColor: ccDisabled
              ? V3_COLORS.callDimmedBorder
              : showCC
                ? V3_COLORS.orange
                : '#ece6dc',
            borderWidth: 1,
          }}
        >
          <span
            className="text-[13.5px] font-bold"
            style={{
              letterSpacing: '.03em',
              color: ccDisabled
                ? V3_COLORS.callDimmedIcon
                : showCC
                  ? V3_COLORS.orange
                  : '#8a7c68',
            }}
          >
            CC
          </span>
        </button>
        {isDesktop && (
          <span
            className={cn(
              'text-[12px]',
              // [Turn 16] ccDisabled's text-[#b3a894] literal matches
              // design-tokens.ts's `callDimmedText` (see the Mute label's
              // identical note above).
              ccDisabled
                ? 'font-medium text-[#b3a894]'
                : showCC
                  ? 'font-semibold text-[#E36001]'
                  : 'font-medium text-[#8a7c68]'
            )}
          >
            {showCC ? 'On' : 'Off'}
          </span>
        )}
      </div>
    </div>
  );
}
